/**
 * iter87.407 (user-requested) — Rivalry Danger Zone card (League scope).
 *
 * Rivalry admins get ONE lifecycle action here: Close. Closing makes
 * the rivalry read-only for everyone (including the admin) — no new
 * tournaments, picks, drafts, or edits — but it stays fully viewable
 * (leaderboard, recaps, history). Unlike the old Archive button (which
 * was a one-way mistake corrected here), Close is reversible by the
 * admin themselves via Reopen. Archive is now SITE-ADMIN ONLY, either
 * manually from Rivalry Management or automatically 30 days after a
 * free-tier rivalry closes.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Lock, Unlock, X, Loader2, Archive } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogPortal, DialogOverlay } from "@/components/ui/dialog";
import { api } from "@/lib/api";

export default function LeagueDangerZoneCard({ league, leagueId, onChanged }) {
    const navigate = useNavigate();
    const [showConfirm, setShowConfirm] = useState(false);
    const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [archiving, setArchiving] = useState(false);
    const [reopening, setReopening] = useState(false);
    const archived = !!league?.archived_at;
    const closed = !!league?.closed_at;
    // GET /leagues/{id} has no `subscription` envelope — use the same
    // composite signal AdminPanel uses (pro_plus_ok, fallback subscription_tier).
    const tier = league?.subscription?.effective_tier || league?.subscription_tier || "free";
    const isProPlus = league?.pro_plus_ok === true || tier === "paid" || tier === "comp";

    const doClose = async () => {
        setBusy(true);
        try {
            await api.closeRivalryAsAdmin(leagueId);
            toast.success(`"${league?.name || "Rivalry"}" is now closed — read-only for everyone.`);
            setShowConfirm(false);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Close failed");
        }
        setBusy(false);
    };

    const doReopen = async () => {
        setReopening(true);
        try {
            await api.reopenRivalryAsAdmin(leagueId);
            toast.success(`"${league?.name || "Rivalry"}" reopened — back to normal.`);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reopen failed");
        }
        setReopening(false);
    };

    // iter87.469 — Free-tier-only early archive. Jumps straight to the
    // same end state as the 30-day auto-archive countdown, so the
    // admin can reuse their 1-rivalry-per-email slot right away.
    const doArchiveNow = async () => {
        setArchiving(true);
        try {
            await api.archiveRivalryAsAdminEarly(leagueId);
            // iter87.469.1 (testing_agent-caught) — the generic `onChanged()`
            // refetch 423's immediately on an archived league and bounces to
            // /hiatus before this toast is ever seen, and /hiatus has no
            // "start a new rivalry" CTA even though that's the whole point
            // of archiving early. Go straight to /start instead, after a
            // beat so the toast is actually visible first.
            toast.success(`"${league?.name || "Rivalry"}" archived — redirecting you to start a new rivalry...`);
            setShowArchiveConfirm(false);
            setTimeout(() => navigate("/start"), 1200);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Archive failed");
            setArchiving(false);
        }
    };

    const handleOpenChange = (open) => {
        if (!open && !busy) setShowConfirm(false);
    };

    const handleArchiveOpenChange = (open) => {
        if (!open && !archiving) setShowArchiveConfirm(false);
    };

    return (
        <div className="brutal-card border-red-500/40 p-4 space-y-3" data-testid="league-danger-zone-card">
            <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <h3 className="font-display font-black text-sm uppercase tracking-wide text-red-300">
                    Danger Zone
                </h3>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
                {isProPlus
                    ? "Lifecycle actions live here. Only a site admin can archive a rivalry (fully locking everyone out) — your only action is Close, and you can always reverse it yourself."
                    : "Lifecycle actions live here. Free-tier rivalries close automatically once their tournament completes, and a closed rivalry can only be reopened by upgrading to Pro+."}
            </p>

            <div className="border border-red-500/30 rounded p-3 space-y-2" data-testid="league-danger-close-block">
                <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-red-400" />
                    <p className="font-display font-extrabold text-xs uppercase tracking-wide text-red-300">
                        Close Rivalry
                    </p>
                </div>
                <ul className="text-[11px] text-zinc-400 space-y-1 list-disc pl-5">
                    <li>The rivalry becomes <strong className="text-white">read-only</strong> — no new tournaments, picks, drafts, or edits — for everyone, including you.</li>
                    <li>Everything stays <strong className="text-white">viewable</strong> — leaderboard, recaps, history, hamburger menu.</li>
                    {isProPlus && (
                        <li>This is <strong className="text-white">reversible by you</strong> — hit Reopen anytime to go back to normal.</li>
                    )}
                </ul>

                {archived ? (
                    <div className="text-[11px] text-zinc-500 italic pt-1" data-testid="league-danger-archived-notice">
                        This rivalry has been archived by a site admin
                        {league?.archived_by ? <> (<strong className="text-zinc-300">{league.archived_by}</strong>)</> : null}.
                        Contact a site admin to restore it.
                    </div>
                ) : closed ? (
                    <div className="space-y-2 pt-1">
                        <div className="text-[11px] text-zinc-500 italic" data-testid="league-danger-closed-notice">
                            This rivalry is closed (read-only)
                            {league?.closed_by ? <> — closed by <strong className="text-zinc-300">
                                {league.closed_by === "system:free-tier-lifecycle" ? "automatically (free tier)" : league.closed_by}
                            </strong></> : null}.
                        </div>
                        {isProPlus ? (
                            <button
                                type="button"
                                onClick={doReopen}
                                disabled={reopening}
                                data-testid="league-danger-reopen-btn"
                                className="brutal-btn-ghost text-xs py-2 px-3 flex items-center gap-1.5 !border-volt/60 !text-volt hover:!bg-volt/10 disabled:opacity-40"
                            >
                                {reopening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
                                Reopen Rivalry
                            </button>
                        ) : (
                            <>
                                <div className="text-[11px] text-zinc-500 italic" data-testid="league-danger-free-tier-closed-notice">
                                    A closed free-tier rivalry can only be reopened by signing up
                                    for a Pro+ account.
                                </div>
                                {/* iter87.469 (operator-requested) — Early archive, free tier
                                    only, only while already Closed. Same confirm-modal pattern
                                    as Close above (and tournament Mark Complete) — never a
                                    native browser confirm(). */}
                                {!archived && (
                                    <div className="border border-heat/30 rounded p-3 space-y-2 mt-2" data-testid="league-danger-early-archive-block">
                                        <p className="text-[11px] text-zinc-400 leading-relaxed">
                                            Free tier is only 1 rivalry per user. To start a new rivalry, archive this
                                            rivalry now — then start a new one.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => setShowArchiveConfirm(true)}
                                            disabled={archiving}
                                            data-testid="league-danger-archive-now-btn"
                                            className="brutal-btn-ghost text-xs py-2 px-3 flex items-center gap-1.5 !border-heat/60 !text-heat hover:!bg-heat/10 disabled:opacity-40"
                                        >
                                            <Archive className="w-3.5 h-3.5" />
                                            Archive Now
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : isProPlus ? (
                    <button
                        type="button"
                        onClick={() => setShowConfirm(true)}
                        disabled={busy}
                        data-testid="league-danger-close-btn"
                        className="brutal-btn-ghost text-xs py-2 px-3 flex items-center gap-1.5 !border-heat/60 !text-heat hover:!bg-heat/10 disabled:opacity-40"
                    >
                        <Lock className="w-3.5 h-3.5" />
                        Close Rivalry
                    </button>
                ) : (
                    <div className="text-[11px] text-zinc-500 italic pt-1" data-testid="league-danger-free-tier-notice">
                        Free-tier rivalries close automatically once their tournament
                        completes. Upgrade to Pro+ to close on your own schedule.
                    </div>
                )}
            </div>

            <Dialog open={showConfirm && !archived && !closed} onOpenChange={handleOpenChange}>
                <DialogPortal>
                    <DialogOverlay />
                    <DialogPrimitive.Content
                        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
                        onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
                        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
                        className="fixed left-[50%] top-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%] w-full max-w-md p-0 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:duration-200 data-[state=closed]:duration-150"
                        data-testid="league-danger-close-modal"
                    >
                        <div className="brutal-card w-full p-5">
                            <div className="flex items-start gap-3 mb-4">
                                <DialogPrimitive.Title asChild>
                                    <h2 className="font-display font-black text-lg uppercase tracking-tight flex-1 text-red-300">
                                        Close this rivalry?
                                    </h2>
                                </DialogPrimitive.Title>
                                <DialogPrimitive.Close asChild>
                                    <button
                                        type="button"
                                        disabled={busy}
                                        data-testid="league-danger-close-modal-close"
                                        className="brutal-btn-ghost text-xs p-1 disabled:opacity-40"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </DialogPrimitive.Close>
                            </div>
                            <div className="flex items-start gap-2 bg-red-500/5 border border-red-500/40 p-3 rounded mb-4">
                                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                <div className="text-xs">
                                    <p className="font-bold text-red-300 mb-1">You can reverse this yourself.</p>
                                    <DialogPrimitive.Description asChild>
                                        <p className="text-zinc-300 leading-relaxed">
                                            <strong className="text-white">{league?.name}</strong> will become read-only for
                                            every crewmate — including you. Hit <strong className="text-white">Reopen</strong> anytime
                                            to go back to normal.
                                        </p>
                                    </DialogPrimitive.Description>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <DialogPrimitive.Close asChild>
                                    <button
                                        type="button"
                                        disabled={busy}
                                        data-testid="league-danger-close-cancel"
                                        className="brutal-btn-ghost text-xs py-2 flex-1 disabled:opacity-40"
                                    >
                                        Keep it open
                                    </button>
                                </DialogPrimitive.Close>
                                <button
                                    type="button"
                                    onClick={doClose}
                                    disabled={busy}
                                    data-testid="league-danger-close-confirm"
                                    className="brutal-btn-ghost text-xs py-2 flex-1 disabled:opacity-40 !border-heat/60 !text-heat hover:!bg-heat/10"
                                >
                                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />}
                                    Close rivalry
                                </button>
                            </div>
                        </div>
                    </DialogPrimitive.Content>
                </DialogPortal>
            </Dialog>

            <Dialog open={showArchiveConfirm} onOpenChange={handleArchiveOpenChange}>
                <DialogPortal>
                    <DialogOverlay />
                    <DialogPrimitive.Content
                        onEscapeKeyDown={(e) => { if (archiving) e.preventDefault(); }}
                        onPointerDownOutside={(e) => { if (archiving) e.preventDefault(); }}
                        onInteractOutside={(e) => { if (archiving) e.preventDefault(); }}
                        className="fixed left-[50%] top-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%] w-full max-w-md p-0 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:duration-200 data-[state=closed]:duration-150"
                        data-testid="league-danger-archive-modal"
                    >
                        <div className="brutal-card w-full p-5">
                            <div className="flex items-start gap-3 mb-4">
                                <DialogPrimitive.Title asChild>
                                    <h2 className="font-display font-black text-lg uppercase tracking-tight flex-1 text-red-300">
                                        Archive this rivalry now?
                                    </h2>
                                </DialogPrimitive.Title>
                                <DialogPrimitive.Close asChild>
                                    <button
                                        type="button"
                                        disabled={archiving}
                                        data-testid="league-danger-archive-modal-close"
                                        className="brutal-btn-ghost text-xs p-1 disabled:opacity-40"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </DialogPrimitive.Close>
                            </div>
                            <div className="flex items-start gap-2 bg-red-500/5 border border-red-500/40 p-3 rounded mb-4">
                                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                <div className="text-xs">
                                    <p className="font-bold text-red-300 mb-1">This can't be undone by you.</p>
                                    <DialogPrimitive.Description asChild>
                                        <p className="text-zinc-300 leading-relaxed">
                                            You and your crew will lose access to <strong className="text-white">{league?.name}</strong> once
                                            it's archived — only a site admin can restore it. You'll be free to start a new rivalry
                                            with this same email right after.
                                        </p>
                                    </DialogPrimitive.Description>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <DialogPrimitive.Close asChild>
                                    <button
                                        type="button"
                                        disabled={archiving}
                                        data-testid="league-danger-archive-cancel"
                                        className="brutal-btn-ghost text-xs py-2 flex-1 disabled:opacity-40"
                                    >
                                        Keep it closed
                                    </button>
                                </DialogPrimitive.Close>
                                <button
                                    type="button"
                                    onClick={doArchiveNow}
                                    disabled={archiving}
                                    data-testid="league-danger-archive-confirm"
                                    className="brutal-btn-ghost text-xs py-2 flex-1 disabled:opacity-40 !border-heat/60 !text-heat hover:!bg-heat/10"
                                >
                                    {archiving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
                                    Archive now
                                </button>
                            </div>
                        </div>
                    </DialogPrimitive.Content>
                </DialogPortal>
            </Dialog>
        </div>
    );
}
